import { isReviewStatus, type ReviewStatus } from "@zenforge/shared";
/**
 * Filtering and severity classification for the pipeline execution list.
 *
 * Pure functions, separated from the component so the rules that decide "does
 * this run need a human" can be tested without a DOM — that classification is
 * the whole point of the screen, and it is easy to get subtly wrong.
 */

export type ExecutionSeverity = "failed" | "attention" | "running" | "done" | "idle";

export type StatusSegment = "all" | "attention" | "running" | "completed" | "archived";

/**
 * "today" is not the same window as "1d": today means since local midnight,
 * which shrinks through the day, while 1d is a rolling 24 hours that reaches
 * back into yesterday. Both are kept because operators ask both questions —
 * "what have we run today" and "what has happened recently".
 *
 * "custom" pairs with the filters' customFrom/customTo — unlike every other
 * range it is not resolvable from `now` alone, so rangeCutoff() (which only
 * ever produces a lower bound) does not handle it; see
 * customRangeBounds() below.
 */
export type TimeRange = "today" | "1d" | "7d" | "30d" | "all" | "custom";

/** What the list needs to classify a row. A structural subset of the API's
 *  execution shape, so tests need not build the whole enriched graph. */
export interface ClassifiableExecution {
  status?: string | null;
  createdAt?: string | null;
  targetName?: string | null;
  collectionId?: number | null;
  pipelineId?: number | null;
  collection?: { id?: number | null; name?: string | null } | null;
  pipeline?: { id?: number | null; name?: string | null } | null;
  generatedContent?: { pushStatus?: string | null; status?: string | null } | null;
}

/**
 * The row's severity, which drives its left stripe.
 *
 * A failed push outranks a COMPLETED execution status: the run finished, but
 * the content never reached the main app, and that is precisely the case a
 * green row would hide. It is reported as "failed" rather than a separate
 * state because the operator's next action is the same — go look at it.
 */
export function severityOf(exec: ClassifiableExecution): ExecutionSeverity {
  if (exec.generatedContent?.pushStatus === "FAILED") return "failed";

  switch (exec.status) {
    case "FAILED":
      return "failed";
    case "PAUSED":
      return "attention";
    case "PROCESSING":
      return "running";
    case "COMPLETED":
    case "APPROVED":
      return "done";
    default:
      // PENDING, CANCELLED, and anything the server adds later. Neutral
      // rather than assumed-healthy: an unknown state is not a green state.
      return "idle";
  }
}

/**
 * Whether a run is waiting on a person.
 *
 * This is the definition behind the "Needs attention" count and segment, and
 * it deliberately unions three distinct causes — a failed run, a paused run,
 * and a run whose content failed to publish — because they share an outcome:
 * nothing further happens until someone intervenes.
 */
export function needsAttention(exec: ClassifiableExecution): boolean {
  const severity = severityOf(exec);
  return severity === "failed" || severity === "attention";
}

/**
 * Cutoff for a time range, or null when the range is unbounded.
 *
 * `today` resolves against the viewer's local midnight rather than UTC: an
 * operator in IST asking "what ran today" means their day, and a UTC boundary
 * would silently drop the first 5.5 hours of it.
 */
export function rangeCutoff(range: TimeRange, now: number): number | null {
  switch (range) {
    case "today": {
      const midnight = new Date(now);
      midnight.setHours(0, 0, 0, 0);
      return midnight.getTime();
    }
    case "1d":
      return now - 24 * 60 * 60 * 1000;
    case "7d":
      return now - 7 * 24 * 60 * 60 * 1000;
    case "30d":
      return now - 30 * 24 * 60 * 60 * 1000;
    default:
      return null;
  }
}

/**
 * The [from, to) instants for a custom range, from the `<input type="date">`
 * values (local "YYYY-MM-DD", no time component) the picker produces.
 *
 * `to` is exclusive and pushed to the *next* local midnight, so picking the
 * same day for both ends still includes everything created that day — an
 * inclusive end at that day's own midnight would match nothing, since every
 * row has a nonzero time component.
 */
export function customRangeBounds(
  customFrom: string | null,
  customTo: string | null,
): { from: number | null; to: number | null } {
  const parseLocalMidnight = (dateStr: string): number | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!match) return null;
    const [, y, m, d] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  };

  const from = customFrom ? parseLocalMidnight(customFrom) : null;
  const toStart = customTo ? parseLocalMidnight(customTo) : null;
  const to = toStart !== null ? toStart + 24 * 60 * 60 * 1000 : null;

  return { from, to };
}

/** Sort keys the server accepts. Kept in step with EXECUTION_SORT_COLUMNS on
 *  the server, which rejects anything outside the set. */
export type ExecutionSort =
  | "createdAt"
  | "startedAt"
  | "completedAt"
  | "status"
  | "cost"
  | "duration";

export type SortDirection = "asc" | "desc";

/** Whether the row's content was actually approved and pushed — a third axis
 *  independent of both the segment tabs (machine lifecycle) and reviewStatus
 *  (the advisory verdict). null means "any". */
export type ApprovalStatus = "approved" | "not_approved";

export interface ExecutionFilters {
  segment: StatusSegment;
  search: string;
  range: TimeRange;
  /** "YYYY-MM-DD" from a date input, local to the viewer. Only meaningful
   *  when range === "custom" — see customRangeBounds(). Kept even when
   *  another range is picked, so switching back to "custom" restores the
   *  last dates instead of forcing them to be re-entered. */
  customFrom: string | null;
  customTo: string | null;
  /** null means "any" — an explicit absence, not an unset field. */
  collectionId: number | null;
  pipelineId: number | null;
  /** The main app's chapter UUID, or null for "any". Unlike collectionId /
   *  pipelineId this is a string — chapters live in the main app's own id
   *  space, not a local auto-increment column. */
  chapterId: string | null;
  /** The human reviewer verdict, or null for "any". Independent of `segment`:
   *  a senior looking for what to push filters RECOMMENDED across all of them. */
  reviewStatus: ReviewStatus | null;
  /** Whether the row was actually approved and pushed, or null for "any". */
  approvalStatus: ApprovalStatus | null;
  sort: ExecutionSort;
  direction: SortDirection;
  /** 1-based, matching what the pager shows. */
  page: number;
  pageSize: number;
}

export const DEFAULT_PAGE_SIZE = 25;

/** The sizes the picker offers. 100 is the ceiling the server enforces, so the
 *  largest choice here is the largest page that exists — the dropdown cannot
 *  ask for one the API will silently clamp. */
export const PAGE_SIZES = [10, 25, 50, 100];

export const SORT_KEYS: ExecutionSort[] = [
  "createdAt",
  "startedAt",
  "completedAt",
  "status",
  "cost",
  "duration",
];

/**
 * The nearest offered page size at or above a requested one.
 *
 * A hand-edited URL can name any number; anything the picker cannot represent
 * would leave the control blank, so the value is moved onto the scale rather
 * than passed through. Oversized values land on the largest option, which is
 * also the server's cap.
 */
export function normalizePageSize(requested: number | null): number {
  if (requested === null) return DEFAULT_PAGE_SIZE;
  return PAGE_SIZES.find((size) => size >= requested) ?? PAGE_SIZES[PAGE_SIZES.length - 1]!;
}

// MySQL DATETIME text, in UTC, matching how mysqlDateTime() writes rows. An
// ISO string with a "Z" compares as a string against these columns and
// silently matches nothing.
function toMysqlDateTime(instant: number): string {
  return new Date(instant).toISOString().slice(0, 19).replace("T", " ");
}

/**
 * Sets createdAfter/createdBefore from a filter's range, shared by
 * apiQueryFromFilters and queueApiQueryFromFilters.
 *
 * The named presets resolve to an instant here rather than being sent by
 * name: "today" means the viewer's local midnight, and only the browser knows
 * where that falls. "custom" is the one range with an upper bound — the
 * presets are all open-ended toward `now`.
 */
function applyRangeParams(
  params: URLSearchParams,
  filters: Pick<ExecutionFilters, "range" | "customFrom" | "customTo">,
  now: number,
): void {
  if (filters.range === "custom") {
    const { from, to } = customRangeBounds(filters.customFrom, filters.customTo);
    if (from !== null) params.set("createdAfter", toMysqlDateTime(from));
    if (to !== null) params.set("createdBefore", toMysqlDateTime(to));
    return;
  }

  const cutoff = rangeCutoff(filters.range, now);
  if (cutoff !== null) params.set("createdAfter", toMysqlDateTime(cutoff));
}

/**
 * The filters as the API expects them.
 *
 * The named time range is resolved to an instant here rather than sent by
 * name: "today" means the viewer's local midnight, and only the browser knows
 * where that falls. Resolving it server-side in UTC would silently drop the
 * early hours of a day for anyone east of it.
 */
export function apiQueryFromFilters(filters: ExecutionFilters, now: number = Date.now()): string {
  const params = new URLSearchParams();

  if (filters.segment !== "all") params.set("segment", filters.segment);
  if (filters.search.trim()) params.set("search", filters.search.trim());
  if (filters.collectionId !== null) params.set("collectionId", String(filters.collectionId));
  if (filters.pipelineId !== null) params.set("pipelineId", String(filters.pipelineId));
  if (filters.chapterId !== null) params.set("chapterId", filters.chapterId);
  if (filters.reviewStatus !== null) params.set("reviewStatus", filters.reviewStatus);
  if (filters.approvalStatus !== null) params.set("approvalStatus", filters.approvalStatus);

  applyRangeParams(params, filters, now);

  if (filters.sort !== "createdAt") params.set("sort", filters.sort);
  if (filters.direction !== "desc") params.set("direction", filters.direction);
  if (filters.page > 1) params.set("page", String(filters.page));
  if (filters.pageSize !== DEFAULT_PAGE_SIZE) params.set("pageSize", String(filters.pageSize));

  const query = params.toString();
  return query ? `?${query}` : "";
}

/** Validates a range param, shared by filtersFromSearch and
 *  queueFiltersFromSearch. Falls back to the given default rather than
 *  passing through an unrecognized value from a hand-edited URL. */
function parseTimeRange(raw: string | null, fallback: TimeRange): TimeRange {
  return raw === "today" ||
    raw === "1d" ||
    raw === "7d" ||
    raw === "30d" ||
    raw === "all" ||
    raw === "custom"
    ? raw
    : fallback;
}

/** Reads filter state out of a URL query string, falling back to the defaults
 *  a first-time visitor should see: everything from the last week. */
export function filtersFromSearch(search: string): ExecutionFilters {
  const params = new URLSearchParams(search);
  const segment = params.get("status");

  // A non-numeric id in a hand-edited URL becomes "any" rather than NaN,
  // which would match nothing and look like an empty database.
  const numeric = (raw: string | null): number | null => {
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isInteger(value) && value > 0 ? value : null;
  };

  const sort = params.get("sort");
  const direction = params.get("dir");
  const pageSize = numeric(params.get("size"));

  return {
    segment:
      segment === "attention" || segment === "running" || segment === "completed" || segment === "archived"
        ? segment
        : "all",
    search: params.get("q") ?? "",
    range: parseTimeRange(params.get("range"), "7d"),
    customFrom: params.get("from") || null,
    customTo: params.get("to") || null,
    collectionId: numeric(params.get("collection")),
    pipelineId: numeric(params.get("pipeline")),
    // A main-app UUID, not a local id — kept as-is rather than run through
    // numeric(), and an empty string (missing param) becomes "any".
    chapterId: params.get("chapter") || null,
    reviewStatus: isReviewStatus(params.get("review") ?? "")
      ? (params.get("review") as ReviewStatus)
      : null,
    approvalStatus:
      params.get("approval") === "approved" || params.get("approval") === "not_approved"
        ? (params.get("approval") as ApprovalStatus)
        : null,
    sort: (SORT_KEYS as string[]).includes(sort ?? "") ? (sort as ExecutionSort) : "createdAt",
    direction: direction === "asc" ? "asc" : "desc",
    page: numeric(params.get("page")) ?? 1,
    // Snapped to a size the picker offers, not merely capped: a value the
    // dropdown has no option for renders it blank, leaving the operator unable
    // to see what page size is actually in effect. Rounding up to the next
    // offered size also keeps the server's 100 ceiling.
    pageSize: normalizePageSize(pageSize),
  };
}

/**
 * The query string for a set of filters, so a run can be linked to whoever
 * owns it. Defaults are omitted rather than serialised — a shared URL should
 * carry what was deliberately chosen, not the whole state object.
 */
export function searchFromFilters(filters: ExecutionFilters): string {
  const params = new URLSearchParams();
  if (filters.segment !== "all") params.set("status", filters.segment);
  if (filters.search.trim()) params.set("q", filters.search.trim());
  if (filters.range !== "7d") params.set("range", filters.range);
  if (filters.range === "custom" && filters.customFrom) params.set("from", filters.customFrom);
  if (filters.range === "custom" && filters.customTo) params.set("to", filters.customTo);
  if (filters.collectionId !== null) params.set("collection", String(filters.collectionId));
  if (filters.pipelineId !== null) params.set("pipeline", String(filters.pipelineId));
  if (filters.chapterId !== null) params.set("chapter", filters.chapterId);
  if (filters.reviewStatus !== null) params.set("review", filters.reviewStatus);
  if (filters.approvalStatus !== null) params.set("approval", filters.approvalStatus);
  if (filters.sort !== "createdAt") params.set("sort", filters.sort);
  if (filters.direction !== "desc") params.set("dir", filters.direction);
  // The page is deliberately not persisted to storage or the URL. A shared
  // link should open on the run being pointed at, and returning to the screen
  // should start at the top rather than on page 7 of a list that has since
  // moved on.
  if (filters.pageSize !== DEFAULT_PAGE_SIZE) params.set("size", String(filters.pageSize));
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const FILTERS_STORAGE_KEY = "zenforge_execution_filters";

/**
 * Whether a query string carries any filter of its own.
 *
 * This is the precedence test: a pasted or bookmarked link must win over
 * whatever this browser last had selected, or a shared "here is the broken
 * run" URL would silently open on the recipient's own filters instead.
 */
export function hasFilterParams(search: string): boolean {
  const params = new URLSearchParams(search);
  return [
    "status",
    "q",
    "range",
    "from",
    "to",
    "collection",
    "pipeline",
    "chapter",
    "review",
    "approval",
    "sort",
    "dir",
    "size",
  ].some((k) => params.has(k));
}

/**
 * The filters to start from: the URL when it specifies any, otherwise the
 * last set this browser used, otherwise the defaults.
 *
 * Storage is a convenience for returning to your own workspace, so it yields
 * to an explicit link every time.
 */
export function initialFilters(
  search: string,
  read: () => string | null = () => localStorage.getItem(FILTERS_STORAGE_KEY),
): ExecutionFilters {
  if (hasFilterParams(search)) return filtersFromSearch(search);

  try {
    // The read itself is inside the guard: Safari private mode and blocked
    // site data throw on ACCESS, not just on a missing key, so wrapping only
    // the parse would still let the page fail on load.
    const stored = read();
    if (stored === null) return filtersFromSearch("");

    // Round-tripped through the query-string parser rather than JSON.parse'd
    // into the object directly, so a stored value from an older shape is
    // validated by exactly the same rules a hand-edited URL is.
    return filtersFromSearch(stored);
  } catch {
    return filtersFromSearch("");
  }
}

/** Persists the current filters. Failures are swallowed: losing a preference
 *  must never take the page down with it. */
export function persistFilters(
  filters: ExecutionFilters,
  write: (value: string) => void = (value) =>
    localStorage.setItem(FILTERS_STORAGE_KEY, value),
): void {
  try {
    write(searchFromFilters(filters));
  } catch {
    // Quota exceeded, or storage blocked entirely. The selection still works
    // for this session; it just will not be there next time.
  }
}

export interface QueueFilters {
  segment: StatusSegment;
  search: string;
  range: TimeRange;
  /** Mirrors ExecutionFilters.customFrom / .customTo. */
  customFrom: string | null;
  customTo: string | null;
  collectionId: number | null;
  categoryId: number | null;
  /** The main app's chapter UUID, or null for "any" — mirrors
   *  ExecutionFilters.chapterId. */
  chapterId: string | null;
  /** The human reviewer verdict, or null for "any" — mirrors
   *  ExecutionFilters.reviewStatus for pipeline executions. */
  reviewStatus: ReviewStatus | null;
  /** Mirrors ExecutionFilters.approvalStatus. */
  approvalStatus: ApprovalStatus | null;
  sort: ExecutionSort;
  direction: SortDirection;
  page: number;
  pageSize: number;
}

export const QUEUE_FILTERS_STORAGE_KEY = "zenforge_queue_filters";

export function queueApiQueryFromFilters(filters: QueueFilters, now: number = Date.now()): string {
  const params = new URLSearchParams();

  if (filters.segment !== "all") params.set("segment", filters.segment);
  if (filters.search.trim()) params.set("search", filters.search.trim());
  if (filters.collectionId !== null) params.set("collectionId", String(filters.collectionId));
  if (filters.categoryId !== null) params.set("categoryId", String(filters.categoryId));
  if (filters.chapterId !== null) params.set("chapterId", filters.chapterId);
  if (filters.reviewStatus !== null) params.set("reviewStatus", filters.reviewStatus);
  if (filters.approvalStatus !== null) params.set("approvalStatus", filters.approvalStatus);

  applyRangeParams(params, filters, now);

  if (filters.sort !== "createdAt") params.set("sort", filters.sort);
  if (filters.direction !== "desc") params.set("direction", filters.direction);
  if (filters.page > 1) params.set("page", String(filters.page));
  if (filters.pageSize !== DEFAULT_PAGE_SIZE) params.set("pageSize", String(filters.pageSize));

  const query = params.toString();
  return query ? `?${query}` : "";
}

export function queueFiltersFromSearch(search: string): QueueFilters {
  const params = new URLSearchParams(search);
  const segment = params.get("status");

  const numeric = (raw: string | null): number | null => {
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isInteger(value) && value > 0 ? value : null;
  };

  const sort = params.get("sort");
  const direction = params.get("dir");
  const pageSize = numeric(params.get("size"));

  return {
    segment:
      segment === "attention" || segment === "running" || segment === "completed" || segment === "archived"
        ? segment
        : "all",
    search: params.get("q") ?? "",
    range: parseTimeRange(params.get("range"), "7d"),
    customFrom: params.get("from") || null,
    customTo: params.get("to") || null,
    collectionId: numeric(params.get("collection")),
    categoryId: numeric(params.get("category")),
    chapterId: params.get("chapter") || null,
    reviewStatus: isReviewStatus(params.get("review") ?? "") ? (params.get("review") as ReviewStatus) : null,
    approvalStatus:
      params.get("approval") === "approved" || params.get("approval") === "not_approved"
        ? (params.get("approval") as ApprovalStatus)
        : null,
    sort: (SORT_KEYS as string[]).includes(sort ?? "") ? (sort as ExecutionSort) : "createdAt",
    direction: direction === "asc" ? "asc" : "desc",
    page: numeric(params.get("page")) ?? 1,
    pageSize: normalizePageSize(pageSize),
  };
}

export function queueSearchFromFilters(filters: QueueFilters): string {
  const params = new URLSearchParams();
  if (filters.segment !== "all") params.set("status", filters.segment);
  if (filters.search.trim()) params.set("q", filters.search.trim());
  if (filters.range !== "7d") params.set("range", filters.range);
  if (filters.range === "custom" && filters.customFrom) params.set("from", filters.customFrom);
  if (filters.range === "custom" && filters.customTo) params.set("to", filters.customTo);
  if (filters.collectionId !== null) params.set("collection", String(filters.collectionId));
  if (filters.categoryId !== null) params.set("category", String(filters.categoryId));
  if (filters.chapterId !== null) params.set("chapter", filters.chapterId);
  if (filters.reviewStatus !== null) params.set("review", filters.reviewStatus);
  if (filters.approvalStatus !== null) params.set("approval", filters.approvalStatus);
  if (filters.sort !== "createdAt") params.set("sort", filters.sort);
  if (filters.direction !== "desc") params.set("dir", filters.direction);
  if (filters.pageSize !== DEFAULT_PAGE_SIZE) params.set("size", String(filters.pageSize));
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function hasQueueFilterParams(search: string): boolean {
  const params = new URLSearchParams(search);
  return [
    "status",
    "q",
    "range",
    "from",
    "to",
    "collection",
    "category",
    "chapter",
    "review",
    "approval",
    "sort",
    "dir",
    "size",
  ].some((k) => params.has(k));
}

export function queueInitialFilters(
  search: string,
  read: () => string | null = () => localStorage.getItem(QUEUE_FILTERS_STORAGE_KEY),
): QueueFilters {
  if (hasQueueFilterParams(search)) return queueFiltersFromSearch(search);

  try {
    const stored = read();
    if (stored === null) return queueFiltersFromSearch("");
    return queueFiltersFromSearch(stored);
  } catch {
    return queueFiltersFromSearch("");
  }
}

export function queuePersistFilters(
  filters: QueueFilters,
  write: (value: string) => void = (value) =>
    localStorage.setItem(QUEUE_FILTERS_STORAGE_KEY, value),
): void {
  try {
    write(queueSearchFromFilters(filters));
  } catch {
  }
}

